import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Menu,
  X,
  User,
  LogOut,
  ChevronDown,
  Home as HomeIcon,
  Store,
  Landmark,
  Map as MapIcon,
  Users as UsersIcon,
  Tag,
  TrendingUp,
  Sparkles,
  Rocket,
  LayoutDashboard,
  Shield,
  UserCircle2,
  PartyPopper,
  Zap,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useCms } from '../hooks/useCms';
import { useNavPromotions } from '../hooks/useNavPromotions';
import CurrencyToggle from './CurrencyToggle';
import ThemeToggle from './ThemeToggle';
import BetaBadge from './BetaBadge';
import NotificationBell from './NotificationBell';
import LogoutConfirmDialog from './LogoutConfirmDialog';
import SessionTimeoutModal from './SessionTimeoutModal';
import useSessionTimeout from '../hooks/useSessionTimeout';

// ---------------------------------------------------------------------------
// Mega-menu data — keep close to the component so adding/removing items is
// a single-file change. Icons come from lucide-react (already tree-shaken
// elsewhere).
// ---------------------------------------------------------------------------

const MEGA_MENUS = {
  buy: {
    triggerKey: 'nav.buy',
    defaultHref: '/properties?purpose=sale',
    testid: 'nav-mega-buy',
    featured: {
      image:
        'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=480&q=80',
      eyebrow: 'À la une',
      title: 'Nouveaux développements à Douala',
      sub: 'Programmes neufs avec piscine, sécurité 24/7. Livraison 2026.',
      to: '/developments',
      testid: 'nav-buy-featured',
    },
    items: [
      {
        icon: HomeIcon,
        label: 'Tous les biens à vendre',
        sub: 'Maisons, appartements, villas',
        to: '/properties?purpose=sale',
        testid: 'nav-buy-all',
      },
      {
        icon: Store,
        label: 'Biens commerciaux à vendre',
        sub: 'Bureaux, boutiques, entrepôts',
        to: '/properties?purpose=sale&type=commercial',
        testid: 'nav-buy-commercial',
      },
      {
        icon: TrendingUp,
        label: 'Développements immobiliers',
        sub: 'Programmes neufs et résidences',
        to: '/developments',
        testid: 'nav-buy-developments',
      },
      {
        icon: Landmark,
        label: 'Reprises bancaires',
        sub: 'Opportunités à prix négociés',
        to: '/reprises-bancaires',
        testid: 'nav-buy-bank',
        badge: 'Nouveau',
      },
      {
        icon: MapIcon,
        label: 'Terrains à vendre',
        sub: 'Avec ou sans titre foncier',
        to: '/properties?purpose=sale&type=land',
        testid: 'nav-buy-land',
      },
      {
        icon: UsersIcon,
        label: 'Trouver un agent',
        sub: 'Agents certifiés Realty237',
        to: '/agents',
        testid: 'nav-buy-agents',
      },
    ],
  },
  rent: {
    triggerKey: 'nav.rent',
    defaultHref: '/properties?purpose=rent',
    testid: 'nav-mega-rent',
    featured: {
      image:
        'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=480&q=80',
      eyebrow: 'Tendance',
      title: 'Locations meublées disponibles',
      sub: 'Studios et appartements clé en main, charges incluses.',
      to: '/properties?purpose=rent',
      testid: 'nav-rent-featured',
    },
    items: [
      {
        icon: HomeIcon,
        label: 'Toutes les locations',
        sub: 'Appartements, studios, villas',
        to: '/properties?purpose=rent',
        testid: 'nav-rent-all',
      },
      {
        icon: Sparkles,
        label: 'Appartements meublés',
        sub: 'Locations clé en main',
        to: '/properties?purpose=rent&furnished=true',
        testid: 'nav-rent-furnished',
        badge: 'Nouveau',
      },
      {
        icon: Store,
        label: 'Locaux commerciaux à louer',
        sub: 'Bureaux, boutiques, entrepôts',
        to: '/properties?purpose=rent&type=commercial',
        testid: 'nav-rent-commercial',
      },
      {
        icon: PartyPopper,
        label: 'Salles de Fête',
        sub: 'Mariages, cérémonies, réceptions',
        to: '/properties?purpose=rent&type=event_hall',
        testid: 'nav-rent-event-hall',
        badge: 'Nouveau',
      },
      {
        icon: UsersIcon,
        label: 'Trouver un agent locatif',
        sub: 'Gestion locative & visites',
        to: '/agents',
        testid: 'nav-rent-agents',
      },
      {
        icon: Tag,
        label: 'Louer votre bien',
        sub: 'Publier votre annonce',
        to: '/publier',
        testid: 'nav-rent-publish',
      },
    ],
  },
  land: {
    triggerKey: 'nav.land',
    defaultHref: '/properties?type=land',
    testid: 'nav-mega-land',
    featured: {
      image:
        'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=480&q=80',
      eyebrow: 'Sécurisé',
      title: 'Terrains avec titre foncier',
      sub: 'Documents vérifiés, parcelles bornées et viabilisées.',
      to: '/properties?type=land',
      testid: 'nav-land-featured',
    },
    items: [
      {
        icon: MapIcon,
        label: 'Terrains à vendre',
        sub: 'Vente directe et viabilisés',
        to: '/properties?purpose=sale&type=land',
        testid: 'nav-land-sale',
      },
      {
        icon: MapIcon,
        label: 'Terrains à louer',
        sub: 'Bail emphytéotique, agricole',
        to: '/properties?purpose=rent&type=land',
        testid: 'nav-land-rent',
      },
      {
        icon: HomeIcon,
        label: 'Tous les terrains',
        sub: 'Vue d’ensemble du marché foncier',
        to: '/properties?type=land',
        testid: 'nav-land-all',
      },
    ],
  },
  pro: {
    triggerKey: 'nav.agentsPro',
    defaultHref: '/agents',
    testid: 'nav-mega-pro',
    featured: {
      image:
        'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=480&q=80',
      eyebrow: 'Devenir Agent Pro',
      title: 'Boostez votre activité d’agent',
      sub: 'Outils IA, CRM, leads premium, badge vérifié — tout en un.',
      to: '/agent-pro',
      testid: 'nav-pro-featured',
    },
    items: [
      {
        icon: UsersIcon,
        label: 'Trouver un agent',
        sub: 'Annuaire des agents vérifiés',
        to: '/agents',
        testid: 'nav-pro-find',
      },
      {
        icon: Sparkles,
        label: 'Devenir Agent Pro',
        sub: 'Formules d’abonnement pour agents immobiliers',
        to: '/agent-pro',
        testid: 'nav-pro-become',
        badge: 'NOUVEAU',
      },
      {
        icon: TrendingUp,
        label: 'Mon Tableau de Bord',
        sub: 'Gérer mes annonces et leads',
        to: '/dashboard',
        testid: 'nav-pro-dashboard',
      },
      {
        icon: Tag,
        label: 'Publier une Annonce',
        sub: 'Mettre en vente ou en location',
        to: '/publier',
        testid: 'nav-pro-publish',
      },
      {
        icon: Zap,
        label: 'Data API B2B',
        sub: 'Accès programmatique aux annonces + market intel',
        to: '/developers',
        testid: 'nav-pro-data-api',
        badge: 'NEW',
      },
    ],
  },
};

// ---------------------------------------------------------------------------
// NavMega — hover/focus aware mega-dropdown trigger.
// ---------------------------------------------------------------------------

const CLOSE_DELAY_MS = 120; // small delay so diagonal mouse moves don't close

const NavMega = ({ menuKey, menu, t, livePromo }) => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);

  // Merge live (admin-edited) promo over the built-in default. Backend
  // always returns the slot, so livePromo is rarely missing, but we still
  // keep the static fallback to never break rendering.
  const featured = menu.featured
    ? {
        ...menu.featured,
        image: livePromo?.image || menu.featured.image,
        eyebrow: livePromo?.eyebrow ?? menu.featured.eyebrow,
        title: livePromo?.title || menu.featured.title,
        sub: livePromo?.sub ?? menu.featured.sub,
        to: livePromo?.url || menu.featured.to,
      }
    : null;

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };

  useEffect(() => () => cancelClose(), []);

  // Close on Escape (a11y).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <div
      className="relative"
      onMouseEnter={() => {
        cancelClose();
        setOpen(true);
      }}
      onMouseLeave={scheduleClose}
      onFocus={() => {
        cancelClose();
        setOpen(true);
      }}
      onBlur={(e) => {
        // Close only when focus leaves the entire subtree.
        if (!e.currentTarget.contains(e.relatedTarget)) scheduleClose();
      }}
    >
      <Link
        to={menu.defaultHref}
        className="inline-flex items-center gap-1 text-stone-700 hover:text-brand-600 hover:bg-brand-50 font-medium transition-colors px-2.5 py-1.5 rounded-lg text-sm whitespace-nowrap"
        aria-haspopup="true"
        aria-expanded={open}
        data-testid={`${menu.testid}-trigger`}
      >
        {t(menu.triggerKey)}
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`}
        />
      </Link>

      {/* Panel */}
      <div
        className={`absolute left-0 top-full pt-2 z-40 ${
          open ? 'pointer-events-auto' : 'pointer-events-none'
        }`}
      >
        <div
          className={`bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-stone-200/80 p-2 transition-all duration-150 origin-top flex ${
            featured
              ? 'min-w-[360px] lg:min-w-[640px]'
              : 'min-w-[360px]'
          } ${
            open
              ? 'opacity-100 translate-y-0 scale-100'
              : 'opacity-0 -translate-y-1 scale-[0.98]'
          }`}
          role="menu"
          aria-label={t(menu.triggerKey)}
          data-testid={menu.testid}
        >
          {/* Items column */}
          <div className="flex-1 min-w-[340px]">
            {menu.items.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.testid}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  role="menuitem"
                  className="group flex items-start gap-3 px-3 py-2.5 rounded-xl hover:bg-paper transition-colors"
                  data-testid={item.testid}
                >
                  <div className="flex-shrink-0 mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-600 group-hover:bg-brand-100 transition-colors">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-stone-900 group-hover:text-brand-700">
                        {item.label}
                      </span>
                      {item.badge && (
                        <BetaBadge
                          label={item.badge}
                          variant="amber"
                          // 4.9 — When the badge is "Nouveau" the section is
                          // fully working ; only "BÊTA" should imply
                          // construction. Pass an explicit title to override
                          // the misleading default tooltip.
                          title={String(item.badge).toLowerCase().includes('bêta') || String(item.badge).toLowerCase().includes('beta')
                            ? 'Section en cours de constitution — du contenu sera ajouté prochainement.'
                            : 'Nouveauté Realty237'}
                          testId={`navbar-badge-${item.testid || ''}`}
                        />
                      )}
                    </div>
                    {item.sub && (
                      <p className="text-xs text-stone-500 leading-snug mt-0.5">
                        {item.sub}
                      </p>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Featured visual — desktop wide only (>= lg) */}
          {featured && (
            <Link
              to={featured.to}
              onClick={() => setOpen(false)}
              role="menuitem"
              className="hidden lg:flex relative ml-2 w-[260px] shrink-0 overflow-hidden rounded-xl group"
              data-testid={menu.featured.testid}
              style={{
                backgroundImage: `url(${featured.image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              {/* Dark gradient overlay for legibility */}
              <div className="absolute inset-0 bg-gradient-to-t from-stone-900/85 via-stone-900/40 to-transparent" />
              <div className="relative z-10 mt-auto p-4 text-white">
                {featured.eyebrow && (
                  <span className="inline-block text-label font-bold uppercase tracking-widest text-brand-300 mb-1.5">
                    {featured.eyebrow}
                  </span>
                )}
                <h4 className="font-display font-bold text-base leading-tight mb-1 group-hover:text-brand-200 transition-colors">
                  {featured.title}
                </h4>
                {featured.sub && (
                  <p className="text-xs text-stone-200 leading-snug mb-2 line-clamp-2">
                    {featured.sub}
                  </p>
                )}
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-brand-300 group-hover:gap-1.5 transition-all">
                  Découvrir
                  <ChevronDown className="h-3 w-3 -rotate-90" />
                </span>
              </div>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Navbar
// ---------------------------------------------------------------------------

const Navbar = () => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [mobileOpenSection, setMobileOpenSection] = useState(null);
  const navigate = useNavigate();
  // BUG-002 (pro-audit) — the navbar re-renders when localStorage.user or
  // localStorage.pro_token changes so that a fresh Pro OTP login (which
  // happens same-tab, not via the `storage` event) properly swaps CTAs.
  // We track a version counter bumped by the polling / storage listener
  // effect below.
  const [authVersion, setAuthVersion] = useState(0);
  useEffect(() => {
    // Cross-tab: real `storage` event on writes in other tabs.
    const onStorage = (e) => {
      if (e.key === 'user' || e.key === 'pro_session' || e.key === null) {
        setAuthVersion((v) => v + 1);
      }
    };
    window.addEventListener('storage', onStorage);
    // Same-tab: patch Storage.prototype (assigning on the localStorage object
    // would create real "setItem" keys) so same-page logins update the navbar.
    if (!window.__r237AuthPatched) {
      const proto = Object.getPrototypeOf(localStorage);
      const _setItem = proto.setItem;
      const _removeItem = proto.removeItem;
      const notifyIf = (store, k) => {
        if (store === localStorage && (k === 'user' || k === 'pro_session')) {
          window.dispatchEvent(new CustomEvent('r237:auth-change', { detail: { key: k } }));
        }
      };
      proto.setItem = function setItem(k, v) { _setItem.call(this, k, v); notifyIf(this, k); };
      proto.removeItem = function removeItem(k) { _removeItem.call(this, k); notifyIf(this, k); };
      window.__r237AuthPatched = true;
    }
    const onAuthChange = () => setAuthVersion((v) => v + 1);
    window.addEventListener('r237:auth-change', onAuthChange);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('r237:auth-change', onAuthChange);
    };
  }, []);
  // eslint-disable-next-line no-unused-vars
  const _authV = authVersion; // read so React tracks the dep; the state itself triggers rerender
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  // Read the pro session flag every render so login/logout swap is immediate.
  const proToken = typeof window !== 'undefined' ? localStorage.getItem('pro_session') : null;
  const proAuthed = !!proToken && !user;
  const slogan = useCms('footer.slogan', t('common.appTagline'));
  const { slots: livePromos } = useNavPromotions();

  // No useEffect for auto-close: instead, the mobile panel uses event
  // delegation (onClick at the wrapper) to dismiss when a Link is clicked.
  // This avoids the React Compiler `react-hooks/set-state-in-effect` rule
  // while keeping the same UX.

  const handleLogout = async () => {
    // Security audit M-3 — server also clears the HttpOnly cookie.
    try {
      const { authAPI } = await import('../utils/api');
      await authAPI.logout();
    } catch { /* logout is best-effort — proceed anyway */ }
    localStorage.removeItem('user');
    navigate('/');
    window.location.reload();
  };

  // UX-15 — Logout confirmation dialog
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);

  // UX-15 — 30 min inactivity logout with 2 min warning modal.
  // Only armed for authenticated sessions.
  const {
    showWarning: sessionWarningOpen,
    remainingMs: sessionRemainingMs,
    resetTimer: resetSessionTimer,
  } = useSessionTimeout({
    enabled: !!user,
    totalMs: 30 * 60 * 1000,
    warningMs:  2 * 60 * 1000,
    onTimeout: handleLogout,
  });

  return (
    <nav className="bg-white/80 dark:bg-stone-900/80 backdrop-blur-xl border-b border-stone-200/60 dark:border-stone-800/70 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group" data-testid="navbar-logo">
            <img
              src="/logo-mark.svg"
              alt="Realty237"
              width="40"
              height="40"
              className="h-10 w-10 rounded-xl shadow-sm group-hover:shadow-md group-hover:scale-[1.03] transition"
              draggable="false"
            />
            <div className="hidden lg:flex flex-col">
              <span className="font-display font-bold text-xl text-stone-900 leading-none">Realty237</span>
              <span className="text-label text-stone-500 mt-0.5" data-testid="navbar-slogan">{slogan}</span>
            </div>
            <span className="lg:hidden font-display font-bold text-xl text-stone-900 leading-none">Realty237</span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-0.5 lg:gap-1 flex-1 justify-end min-w-0">
            <NavMega menuKey="buy" menu={MEGA_MENUS.buy} t={t} livePromo={livePromos?.buy} />
            <NavMega menuKey="rent" menu={MEGA_MENUS.rent} t={t} livePromo={livePromos?.rent} />
            <NavMega menuKey="land" menu={MEGA_MENUS.land} t={t} livePromo={livePromos?.land} />
            <Link to="/developments" className="text-stone-700 hover:text-brand-600 hover:bg-brand-50 font-medium transition-colors px-2.5 py-1.5 rounded-lg text-sm whitespace-nowrap" data-testid="navbar-developments-link">
              {t('nav.developments')}
            </Link>
            <NavMega menuKey="pro" menu={MEGA_MENUS.pro} t={t} livePromo={livePromos?.pro} />

            <div className="ml-1 flex items-center gap-1 shrink-0">
            <ThemeToggle />
            <CurrencyToggle />
            {user ? (
              <>
                {user.role !== 'admin' && (
                  <Link
                    to="/dashboard?tab=annonces#boost"
                    data-testid="navbar-boost-cta"
                    title="Mettre une annonce à la une"
                  >
                    <Button
                      size="sm"
                      className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-sm rounded-xl inline-flex items-center gap-1.5"
                    >
                      <Rocket className="h-4 w-4" />
                      Booster
                    </Button>
                  </Link>
                )}
                <Link to={user.role === 'admin' ? '/admin' : '/dashboard'}>
                  <Button variant="outline" size="sm" className="flex items-center gap-2 rounded-xl">
                    <User className="h-4 w-4" />
                    {user.name}
                  </Button>
                </Link>
                <NotificationBell />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="rounded-xl"
                      aria-label="Menu utilisateur"
                      data-testid="navbar-user-menu-trigger"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel className="flex items-center gap-2 py-2">
                      <UserCircle2 className="h-4 w-4 text-stone-500" />
                      <div className="flex flex-col min-w-0">
                        <span className="font-semibold text-stone-900 truncate">
                          {user.name}
                        </span>
                        {user.email && (
                          <span className="text-label text-stone-500 truncate">
                            {user.email}
                          </span>
                        )}
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link
                        to={user.role === 'admin' ? '/admin' : '/dashboard'}
                        data-testid="navbar-user-menu-dashboard"
                      >
                        <LayoutDashboard className="h-4 w-4 mr-2" />
                        {user.role === 'admin' ? 'Administration' : 'Tableau de bord'}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link
                        to="/dashboard?tab=profil"
                        data-testid="navbar-user-menu-profile"
                      >
                        <User className="h-4 w-4 mr-2" />
                        Mon profil
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link
                        to="/dashboard?tab=securite"
                        data-testid="navbar-user-menu-security"
                      >
                        <Shield className="h-4 w-4 mr-2" />
                        Sécurité
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={(e) => { e.preventDefault(); setLogoutConfirmOpen(true); }}
                      className="text-red-600 focus:text-red-700 focus:bg-red-50"
                      data-testid="navbar-user-menu-logout"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Déconnexion
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : proAuthed ? (
              <Link to="/pro-dashboard" data-testid="navbar-pro-chip">
                <Button size="sm" variant="outline" className="rounded-xl whitespace-nowrap border-brand-300 text-brand-700 hover:bg-brand-50">
                  Espace Pro
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/auth?mode=login" data-testid="navbar-login-cta">
                  <Button variant="ghost" size="sm" className="rounded-xl whitespace-nowrap">{t('nav.login')}</Button>
                </Link>
                <Link to="/auth?mode=signup" data-testid="navbar-signup-cta">
                  <Button size="sm" className="bg-brand-600 hover:bg-brand-700 text-white shadow-sm rounded-xl whitespace-nowrap shrink-0">{t('nav.signup')}</Button>
                </Link>
              </>
            )}
            </div>
          </div>

          {/* Mobile Menu Button — audit fix 10.4: 48×48 tap target (was p-2 = 40px). */}
          <div className="lg:hidden flex items-center gap-1">
            <button
              type="button"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex h-12 w-12 items-center justify-center rounded-lg text-stone-700 hover:bg-stone-100 transition-colors"
              aria-label="Menu"
              data-testid="navbar-mobile-toggle"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu — accordion for sections with sub-items */}
        {isMenuOpen && (
          <div
            className="lg:hidden py-4 space-y-1"
            data-testid="navbar-mobile-menu"
            onClick={(e) => {
              // Close the mobile panel when any link inside is clicked.
              if (e.target.closest('a')) {
                setIsMenuOpen(false);
                setMobileOpenSection(null);
              }
            }}
          >
            {Object.entries(MEGA_MENUS).map(([key, menu]) => {
              const isOpen = mobileOpenSection === key;
              return (
                <div key={key} className="rounded-lg">
                  <button
                    type="button"
                    onClick={() => setMobileOpenSection(isOpen ? null : key)}
                    className="w-full flex items-center justify-between px-4 py-2 text-stone-800 hover:bg-stone-100 rounded-lg font-medium"
                    aria-expanded={isOpen}
                    data-testid={`${menu.testid}-mobile-trigger`}
                  >
                    <span>{t(menu.triggerKey)}</span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isOpen && (
                    <div className="pl-3 py-1 space-y-1" data-testid={`${menu.testid}-mobile-panel`}>
                      {menu.items.map((item) => (
                        <Link
                          key={item.testid}
                          to={item.to}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-stone-700 hover:bg-paper rounded-md"
                          data-testid={`${item.testid}-mobile`}
                        >
                          <span className="inline-block w-1 h-1 rounded-full bg-brand-400" />
                          {item.label}
                          {item.badge && (
                            <BetaBadge
                              label={item.badge}
                              variant="amber"
                              title={String(item.badge).toLowerCase().includes('bêta') || String(item.badge).toLowerCase().includes('beta')
                                ? 'Section en cours de constitution — du contenu sera ajouté prochainement.'
                                : 'Nouveauté Realty237'}
                              testId={`navbar-mobile-badge-${item.testid || ''}`}
                            />
                          )}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            <Link to="/developments" className="block px-4 py-2 text-stone-800 hover:bg-stone-100 rounded font-medium">
              {t('nav.developments')}
            </Link>

            <div className="pt-3 mt-3 border-t border-stone-200 space-y-2">
              {user ? (
                <>
                  <Link to={user.role === 'admin' ? '/admin' : '/dashboard'} className="block px-4 py-2 text-stone-800 hover:bg-stone-100 rounded">
                    {user.name}
                  </Link>
                  <button
                    type="button"
                    onClick={() => { setIsMenuOpen(false); setLogoutConfirmOpen(true); }}
                    className="block w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 rounded font-medium"
                    data-testid="navbar-mobile-logout"
                  >
                    {t('nav.logout')}
                  </button>
                </>
              ) : proAuthed ? (
                <Link to="/pro-dashboard" className="block px-4 py-2" data-testid="navbar-mobile-pro-chip">
                  <Button variant="outline" className="w-full border-brand-300 text-brand-700">Espace Pro</Button>
                </Link>
              ) : (
                <>
                  <Link to="/auth?mode=login" className="block px-4 py-2">
                    <Button variant="outline" className="w-full">{t('nav.login')}</Button>
                  </Link>
                  <Link to="/auth?mode=signup" className="block px-4 py-2">
                    <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white">{t('nav.signup')}</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* UX-15 — Logout confirmation + session timeout warning */}
      <LogoutConfirmDialog
        open={logoutConfirmOpen}
        onOpenChange={setLogoutConfirmOpen}
        onConfirm={() => { setLogoutConfirmOpen(false); handleLogout(); }}
      />
      <SessionTimeoutModal
        open={sessionWarningOpen}
        remainingMs={sessionRemainingMs}
        onStay={resetSessionTimer}
        onLogout={handleLogout}
      />
    </nav>
  );
};

export default Navbar;
